// src/config/index.js
// Centralized configuration for AgentBlayna frontend

/**
 * API Base URL - reads from environment variable with fallback for development
 * In production, REACT_APP_API_BASE_URL must be set to the actual backend URL
 */
export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

/**
 * Storage keys - unified across the application
 * Using a single consistent key prevents token fragmentation
 */
export const STORAGE_KEYS = {
  TOKEN: "agentBlayna_token",
  REFRESH_TOKEN: "agentBlayna_refresh_token",
  USER: "agentBlayna_user",
  THEME: "agentBlayna_theme",
  CHAT_HISTORY: "agentBlayna_chat_v1",
};

/**
 * API endpoints - centralized for easy maintenance
 */
export const API_ENDPOINTS = {
  // Auth
  LOGIN: "/api/auth/login",
  LOGOUT: "/api/auth/logout",
  REFRESH: "/api/auth/refresh",
  ME: "/api/auth/me",
  CHANGE_PASSWORD: "/api/auth/change-password",
  COMPLETE_ONBOARDING: "/api/auth/complete-onboarding",
  REGISTER: "/api/auth/register",
  SIGNUP: "/api/auth/signup",
  INVITE_INFO: "/api/auth/invite-info",
  VERIFY: "/api/auth/verify",
  TRIAL_STATUS: "/api/auth/trial-status",

  // Access Request (public)
  ACCESS_REQUEST: "/api/access-request",
  ACCESS_REQUEST_MARKETS: "/api/access-request/markets",

  // Superadmin
  ADMIN_ACCESS_REQUESTS: "/api/admin/access-requests",
  ADMIN_MARKETS: "/api/admin/markets",
  ADMIN_ORGANIZATIONS: "/api/admin/organizations",
  ADMIN_DIRECT_REGISTER: "/api/admin/direct-register",
  ADMIN_RESEND_CREDENTIALS: "/api/admin/users",

  // Superadmin - Stripe Management
  ADMIN_STRIPE_CONFIG: "/api/admin/stripe/config",
  ADMIN_STRIPE_TEST_CONNECTION: "/api/admin/stripe/test-connection",
  ADMIN_STRIPE_STATS: "/api/admin/stripe/stats",
  ADMIN_STRIPE_SYNC: "/api/admin/stripe/sync-products",
  ADMIN_STRIPE_PLANS: "/api/admin/stripe/plans",
  ADMIN_STRIPE_PLANS_BULK_ACTION: "/api/admin/stripe/plans/bulk-action",
  ADMIN_STRIPE_COUPONS: "/api/admin/stripe/coupons",
  ADMIN_STRIPE_WEBHOOK_EVENTS: "/api/admin/stripe/webhook-events",
  ADMIN_STRIPE_WEBHOOK_ENDPOINTS: "/api/admin/stripe/webhook-endpoints",

  // Superadmin - Coverage Reports
  ADMIN_REPORTS_COVERAGE: "/api/admin/reports/coverage",

  // Core resources
  CLIENTS: "/api/clients/",
  CLIENTS_CLEAR_ALL: "/api/clients/all",
  CLIENTS_IMPORT: "/api/clients/import",
  CLIENTS_TEMPLATE: "/api/clients/template",
  DEALS: "/api/deals/",
  LISTINGS: "/api/listings/",
  VENDORS: "/api/vendors/",

  // AI/Chat
  CHAT: "/api/chat",

  // Billing / Subscriptions (new API)
  BILLING_STATUS: "/api/billing/subscription-status",
  BILLING_PLANS: "/api/billing/plans",
  BILLING_SEATS: "/api/billing/seats",
  BILLING_CHECKOUT: "/api/billing/create-checkout-session",
  BILLING_PORTAL: "/api/billing/create-portal-session",
  BILLING_VERIFY_SESSION: "/api/billing/verify-checkout-session",
  BILLING_DETAILS: "/api/billing/subscription-details",

  // Team / Invitations
  INVITES: "/api/invites",
  TEAM_MEMBERS: "/api/team/members",

  // Legacy Stripe endpoints (deprecated, kept for backwards compatibility)
  STRIPE_CHECKOUT: "/api/stripe/create-checkout-session",
  STRIPE_STATUS: "/api/stripe/subscription-status",
  STRIPE_CANCEL: "/api/stripe/cancel",
};

/**
 * Stripe configuration
 */
export const STRIPE_CONFIG = {
  publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "",
};

/**
 * Application settings
 */
export const APP_CONFIG = {
  // Token expiration check buffer (in milliseconds)
  // Check token validity 5 minutes before actual expiration
  TOKEN_EXPIRY_BUFFER: 5 * 60 * 1000,

  // Maximum localStorage chat history entries
  MAX_CHAT_HISTORY: 100,

  // API request timeout (in milliseconds)
  REQUEST_TIMEOUT: 30000,
};

/**
 * Feature flags - can be controlled via environment variables
 */
export const FEATURES = {
  ENABLE_CHAT: process.env.REACT_APP_ENABLE_CHAT !== "false",
  ENABLE_AI_INSIGHTS: process.env.REACT_APP_ENABLE_AI !== "false",
  ENABLE_BILLING: process.env.REACT_APP_ENABLE_BILLING === "true",
  DEBUG_MODE: process.env.NODE_ENV === "development",
};

// Warn in development if using default localhost URL
if (FEATURES.DEBUG_MODE && !process.env.REACT_APP_API_BASE_URL) {
  console.info(
    "[Config] Using default API URL:",
    API_BASE_URL,
    "- Set REACT_APP_API_BASE_URL for production"
  );
}
